/** @jsxRuntime classic */
/** @jsx jsx */
import { gql } from "@apollo/client";
import {
  jsx,
  Box,
  Flex,
  Heading,
  Paragraph,
  ThemeProvider,
  Container,
  Button,
  Image,
  ThemeUICSSObject,
  Divider,
} from "theme-ui";
import CommonHead from "components/common-head";
import CommonPostBody from "components/common-post-body";
import SecondaryLayout from "components/secondary-layout";

import SEO from "components/seo";
import client from "services/apollo";
import { appTheme } from "theme";
import { ProgramOutline, PROGRAM_OUTLINE_FIELDS } from "types";
import pluralize from "pluralize";
import { programHours, programNKlasses } from "helpers/programs";
import { enumToReadable } from "helpers/strings";
import { Link } from "components/link";
import DownloadAppCTA from "components/download-app-cta";

// We use static instead of SSR so it's faster.

export const getStaticProps = async () => {
  const { data } = await client.query({
    query: gql`
      ${PROGRAM_OUTLINE_FIELDS}
      query programOutlines {
        programOutlines {
          ...ProgramOutlineFields
        }
      }
    `,
  });
  return {
    props: {
      outlines: ([...data.programOutlines] as ProgramOutline[]).sort(
        (p1, p2) => p1.createdAt - p2.createdAt
      ),
    },
  };
};

const aspectRatio = 16 / 9;

const OutlineListItem = ({ outline }: { outline: ProgramOutline }) => {
  return (
    <Box>
      <Link
        path={`/app/program/${outline.slug}`}
        sx={{ textDecoration: "none", color: "inherit" }}
      >
        <Flex sx={{ flexDirection: "row" }}>
          <Image
            src={outline.imageURI}
            alt={outline.name}
            sx={styles.programImage}
          />
          <Flex sx={{ flexDirection: "column", flex: 1 }}>
            <Heading as="h2">{outline.name}</Heading>
            <Paragraph
              sx={{
                color: "text_secondary",
                display: ["none", "block", null],
              }}
            >
              {outline.shortDesc}
            </Paragraph>
            <Paragraph variant="smaller" sx={{ color: "text_tertiary" }}>
              {outline.instructor.firstName} {outline.instructor.lastName}
            </Paragraph>
            <Paragraph variant="smaller" sx={{ color: "text_tertiary" }}>
              {pluralize("total hour", programHours(outline), true)}
              {" · "}
              {pluralize("class", programNKlasses(outline), true)}
              {" · "}
              {enumToReadable(outline.skillLevel)}
            </Paragraph>
            <Button sx={{ my: 2 }}>
              {outline.premium ? "Preview Now" : "Watch For Free"}
            </Button>
          </Flex>
        </Flex>
      </Link>
      <Divider my={[3, null, 5, null, 7]} />
    </Box>
  );
};

const Programs = ({ outlines }: { outlines: ProgramOutline[] }) => {
  return (
    <ThemeProvider theme={appTheme}>
      <SecondaryLayout>
        <CommonHead />
        <SEO
          title={"Online Tennis Programs | oneCoach"}
          description={
            "oneCoach offers online tennis programs for beginners, intermediate, and advanced players. Each program contains hours of self-paced lessons & drills that can help you get to the next level."
          }
        />
        <div id="root">
          <CommonPostBody />
          <section sx={{ variant: "section.general" }}>
            <Container py={5}>
              <Heading as="h1" sx={{ textAlign: "center" }}>
                All Tennis Programs & Courses
              </Heading>
              <Box sx={{ py: 20 }}>
                <Paragraph sx={{ width: "100%", textAlign: "center" }}>
                  Whether you're a beginner, intermediate, or advanced tennis
                  player, our online tennis programs are designed from the
                  ground up to get you to the next level.
                </Paragraph>
                <Paragraph sx={{ width: "100%", textAlign: "center" }}>
                  We break down the game of tennis into{" "}
                  <strong>step-by-step lessons and drills</strong> you can watch
                  at your own pace and take to the court with you.
                </Paragraph>
              </Box>

              {outlines.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    my: 8,
                  }}
                >
                  <Box sx={{ my: 2 }}>
                    <Heading as="h2" sx={{ textAlign: "center" }}>
                      No programs could be found.
                    </Heading>
                    <Heading as="h3" sx={{ textAlign: "center" }}>
                      Did you misspell the link 🤔?
                    </Heading>
                  </Box>
                  <Link path="/">
                    <Button>Return Home</Button>
                  </Link>
                </Box>
              ) : (
                <Box sx={{ my: 6 }}>
                  {outlines.map((outline) => (
                    <OutlineListItem key={outline.slug} outline={outline} />
                  ))}
                  <Box my={8}>
                    <Heading as="h3" sx={{ textAlign: "center" }}>
                      More programs coming soon!
                    </Heading>
                    <Paragraph
                      sx={{ color: "text_secondary", textAlign: "center" }}
                    >
                      We produce new content every week... check back in a few
                      days!
                    </Paragraph>
                  </Box>
                </Box>
              )}
            </Container>
          </section>
          <DownloadAppCTA />
        </div>
      </SecondaryLayout>
    </ThemeProvider>
  );
};

const styles: { [key: string]: ThemeUICSSObject } = {
  programImage: {
    width: [100, null, 300, null, 400],
    height: [100, null, 300 / aspectRatio, null, 400 / aspectRatio],
    objectFit: "cover",
    borderRadius: "10%",
    mr: [2, null, 4, null, 8],
  },
};

export default Programs;
